import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { GraduationCap, MapPin, Calendar, Award } from 'lucide-react';

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const education = {
    degree: "Bachelor of Science in Computer Science",
    university: "San Francisco State University",
    location: "San Francisco, CA",
    graduation: "May 2026",
    gpa: "3.6 / 4.00",
    coursework: [
      "Software Design and Development",
      "The Object-Oriented Paradigm",
      "Database Management Systems",
      "Data Structures & Algorithms",
      "Principles of Computing and Operating Systems",
      "Generative AI",
      "NLP",
      "Deep Learning"
    ]
  };

  return (
    <section id="about" className="py-20 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-white mb-4">About Me</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            I'm a passionate Computer Science student at SFSU with a strong foundation in software development, 
            AI/ML, and a drive to create innovative solutions that make a difference.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Education Card */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gray-700 rounded-2xl p-8 shadow-xl"
          >
            <div className="flex items-center mb-6">
              <div className="bg-blue-600 p-3 rounded-full mr-4">
                <GraduationCap className="text-white" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-white">Education</h3>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-xl font-semibold text-white mb-2">
                  {education.degree}
                </h4>
                <div className="flex items-center text-gray-300 mb-1">
                  <MapPin size={16} className="mr-2" />
                  {education.university}
                </div>
                <div className="flex items-center text-gray-300 mb-1">
                  <Calendar size={16} className="mr-2" />
                  {education.graduation}
                </div>
                <div className="flex items-center text-blue-400 font-semibold">
                  <Award size={16} className="mr-2" />
                  GPA: {education.gpa}
                </div>
              </div>

              <div className="pt-4">
                <h5 className="text-lg font-semibold text-white mb-3">Relevant Coursework</h5>
                <div className="grid grid-cols-2 gap-2">
                  {education.coursework.map((course, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                      className="text-gray-300 text-sm bg-gray-600 px-3 py-2 rounded-lg"
                    >
                      {course}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Personal Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">Who I Am</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                I'm a dedicated software engineering intern at Quantum Pulse Consulting, where I'm passionate about 
                building AI-first productivity tools and scalable backend systems. My journey in tech started with 
                full-stack development, and I've evolved to specialize in AI/ML applications.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                I believe in creating technology that not only solves problems but enhances human productivity 
                and decision-making. From smart meeting assistants to behavior-driven habit trackers, I'm 
                constantly exploring innovative ways to leverage AI for better user experiences.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-gray-700 p-4 rounded-xl text-center"
              >
                <div className="text-2xl font-bold text-blue-400">2+</div>
                <div className="text-gray-300">Years Experience</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="bg-gray-700 p-4 rounded-xl text-center"
              >
                <div className="text-2xl font-bold text-blue-400">10+</div>
                <div className="text-gray-300">Technologies</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 